import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { generateUUID } from "@/lib/utils";
import { toFriendlyMutationError } from "@/lib/error-utils";

export interface MeetingNote {
  id: string;
  title: string;
  content: string | null;
  meeting_date: string;
  project_id: string | null;
  project_group_id: string | null;
  document_id: string | null;
  admin_only: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingNoteTag {
  id: string;
  name: string;
  color: string | null;
}

export interface MeetingNoteWithTags extends MeetingNote {
  tags: MeetingNoteTag[];
  mentions: {
    person_id: string;
    people: { id: string; first_name: string; last_name: string };
  }[];
  project?: {
    id: string;
    name: string;
    number: string | null;
    event_start_date?: string | null;
  } | null;
  project_group?: { id: string; name: string; number: string | null } | null;
}

export interface MeetingNoteInsert {
  title: string;
  content?: string | null;
  meeting_date: string;
  project_id?: string | null;
  project_group_id?: string | null;
  tag_ids?: string[];
  mentioned_person_ids?: string[];
  admin_only?: boolean;
}

export interface MeetingNoteUpdate {
  title?: string;
  content?: string | null;
  meeting_date?: string;
  tag_ids?: string[];
  mentioned_person_ids?: string[];
  admin_only?: boolean;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroPersonRow = {
  id: string;
  firstName: string;
  lastName: string;
};

export type ZeroMeetingNoteRow = {
  id: string;
  title: string;
  content?: string | null;
  meetingDate: number | string;
  projectID?: string | null;
  projectGroupID?: string | null;
  documentID?: string | null;
  adminOnly?: boolean | null;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  tagAssignments?: Array<{
    tag?: {
      id: string;
      name: string;
      color?: string | null;
    } | null;
  }> | null;
  mentions?: Array<{
    personID: string;
    people?: ZeroPersonRow | null;
  }> | null;
  project?: {
    id: string;
    name: string;
    number?: string | null;
    eventStartDate?: number | string | null;
  } | null;
  projectGroup?: {
    id: string;
    name: string;
    number?: string | null;
  } | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

export function toLegacyMeetingNoteDate(
  value: number | string | null | undefined,
) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? "";
}

export function toLegacyMeetingNote(
  row: ZeroMeetingNoteRow,
): MeetingNoteWithTags {
  return {
    id: row.id,
    title: row.title,
    content: row.content ?? null,
    meeting_date: toLegacyMeetingNoteDate(row.meetingDate),
    project_id: row.projectID ?? null,
    project_group_id: row.projectGroupID ?? null,
    document_id: row.documentID ?? null,
    admin_only: row.adminOnly ?? false,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    tags: (row.tagAssignments ?? [])
      .filter((assignment) => !!assignment.tag)
      .map((assignment) => ({
        id: assignment.tag!.id,
        name: assignment.tag!.name,
        color: assignment.tag!.color ?? null,
      })),
    mentions: (row.mentions ?? [])
      .filter((mention) => !!mention.people)
      .map((mention) => ({
        person_id: mention.personID,
        people: {
          id: mention.people!.id,
          first_name: mention.people!.firstName,
          last_name: mention.people!.lastName,
        },
      })),
    project: row.project
      ? {
          id: row.project.id,
          name: row.project.name,
          number: row.project.number ?? null,
          event_start_date: toLegacyMeetingNoteDate(row.project.eventStartDate),
        }
      : null,
    project_group: row.projectGroup
      ? {
          id: row.projectGroup.id,
          name: row.projectGroup.name,
          number: row.projectGroup.number ?? null,
        }
      : null,
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  onError,
}: {
  run: (input: TInput) => Promise<TResult>;
  onError: {
    title: string;
    successTitle?: string;
    successDescription?: string;
  };
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          if (onError.successTitle) {
            toast({
              title: onError.successTitle,
              description: onError.successDescription,
            });
          }
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast({
            title: onError.title,
            description: error.message,
            variant: "destructive",
          }),
        );
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useProjectMeetingNotes(projectId: string | undefined) {
  const query = projectId
    ? appQueries.meetingNotes.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const notes = ((data ?? []) as ZeroMeetingNoteRow[]).map(toLegacyMeetingNote);

  return {
    data: notes,
    isLoading: !!projectId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useRelatedProjectMeetingNotes(projectId: string | undefined) {
  const query = projectId
    ? appQueries.meetingNotes.byMentionedProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const notes = ((data ?? []) as ZeroMeetingNoteRow[]).map(toLegacyMeetingNote);

  return {
    data: notes,
    isLoading: !!projectId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectGroupMeetingNotes(
  projectGroupId: string | undefined,
) {
  const query = projectGroupId
    ? appQueries.meetingNotes.byProjectGroup({ projectGroupID: projectGroupId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectGroupId });
  const notes = ((data ?? []) as ZeroMeetingNoteRow[]).map(toLegacyMeetingNote);

  return {
    data: notes,
    isLoading: !!projectGroupId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useMeetingNote(id: string | undefined) {
  const query = id ? appQueries.meetingNotes.byID({ id }) : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!id });
  const note = data ? toLegacyMeetingNote(data as ZeroMeetingNoteRow) : null;

  return {
    data: note,
    isLoading: !!id && details.type !== "complete" && !note,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useMeetingNoteTags() {
  const [data, details] = useZeroQuery(appQueries.meetingNoteTags.list());
  const tags = (
    (data ?? []) as Array<{
      id: string;
      name: string;
      color?: string | null;
    }>
  ).map((tag) => ({
    id: tag.id,
    name: tag.name,
    color: tag.color ?? null,
  }));

  return {
    data: tags,
    isLoading: details.type !== "complete" && tags.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateMeetingNote() {
  const zero = useZero() as ZeroMutateClient;
  const { isOffline } = useOnlineStatus();

  return useZeroMutation<MeetingNoteInsert, MeetingNoteWithTags>({
    onError: {
      title: "Error creating meeting note",
      successTitle: "Meeting note created",
      successDescription: "The meeting note has been created successfully.",
    },
    run: async (input) => {
      if (isOffline) {
        throw new Error("Meeting notes can be created when you are online.");
      }

      const id = generateUUID();
      const documentID = generateUUID();
      const mutation = zero.mutate(
        appMutators.meetingNotes.create({
          id,
          documentID,
          title: input.title,
          content: input.content ?? null,
          meetingDate: input.meeting_date,
          projectID: input.project_id ?? null,
          projectGroupID: input.project_group_id ?? null,
          tagIDs: input.tag_ids ?? [],
          mentionedPersonIDs: input.mentioned_person_ids ?? [],
          adminOnly: input.admin_only ?? false,
        }),
      );
      await mutation.client;
      void reportZeroMutationFailure(
        mutation.server,
        "Meeting note create rejected",
      ).catch((error) =>
        toast({
          title: "Error creating meeting note",
          description: error.message,
          variant: "destructive",
        }),
      );
      return toLegacyMeetingNote({
        id,
        documentID,
        title: input.title,
        content: input.content ?? null,
        meetingDate: input.meeting_date,
        projectID: input.project_id ?? null,
        projectGroupID: input.project_group_id ?? null,
        adminOnly: input.admin_only ?? false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tagAssignments: [],
        mentions: [],
      });
    },
  });
}

export function useUpdateMeetingNote() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { id: string; updates: MeetingNoteUpdate },
    { id: string }
  >({
    onError: {
      title: "Error updating meeting note",
    },
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.meetingNotes.update({
          id,
          title: updates.title,
          content: updates.content,
          meetingDate: updates.meeting_date,
          tagIDs: updates.tag_ids,
          mentionedPersonIDs: updates.mentioned_person_ids,
          adminOnly: updates.admin_only,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Meeting note update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteMeetingNote() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, { id: string }>({
    onError: {
      title: "Error deleting meeting note",
      successTitle: "Meeting note deleted",
      successDescription: "The meeting note has been deleted successfully.",
    },
    run: async (id) => {
      const mutation = zero.mutate(appMutators.meetingNotes.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Meeting note delete rejected",
      );
      return { id };
    },
  });
}

export function useCreateMeetingNoteTag() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { name: string; color?: string | null },
    MeetingNoteTag
  >({
    onError: { title: "Error creating tag" },
    run: async (input) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.meetingNoteTags.create({
          id,
          name: input.name,
          color: input.color ?? null,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Meeting note tag create rejected",
      );
      return { id, name: input.name, color: input.color ?? null };
    },
  });
}

export function useUpdateMeetingNoteTag() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { id: string; name?: string; color?: string | null },
    { id: string }
  >({
    onError: { title: "Error updating tag" },
    run: async (input) => {
      const mutation = zero.mutate(
        appMutators.meetingNoteTags.update({
          id: input.id,
          name: input.name,
          color: input.color,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Meeting note tag update rejected",
      );
      return { id: input.id };
    },
  });
}

export function useDeleteMeetingNoteTag() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    onError: { title: "Error deleting tag" },
    run: async (id) => {
      const mutation = zero.mutate(appMutators.meetingNoteTags.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Meeting note tag delete rejected",
      );
      return id;
    },
  });
}
